/* global Intl */
/**
 * Murphy Door Configurator - Price Calculator Business Logic
 *
 * PURPOSE: Calculates total price by managing base product price calculations,
 * processing selection price additions, handling price formatting and validation,
 * and providing error-handled price parsing.
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: Business Logic (bl-)
 * GOLDEN STANDARD REFERENCE: md-config-bl-material-selector.js (200/200)
 *
 * CCP COMPLIANCE:
 * - CONSTANTS.EVENTS.* exclusively (no local EVENT_TYPES - Pattern 1 & 19)
 * - publishEvent() async helper with try/catch (Pattern 2, 11)
 * - 5-stage event lifecycle: STARTED -> VALIDATION -> PROGRESS -> COMPLETED/FAILED (Pattern 3)
 * - Event consumer handler for bootstrap (Pattern 4)
 * - Bootstrap runtime protection via SYSTEM_STATE_FROM_PPD_READY (Pattern 6)
 * - PPD-first O(1) data access via conductor delegation (Patterns 7, 8)
 * - Data access through conductor (Pattern 9)
 * - UI updates through conductor delegation (Pattern 10)
 * - Performance tracking with thresholds (Patterns 12, 13)
 * - Try/catch + FAILED events on all operations (Pattern 14)
 * - Cleanup arrays for all subscriptions (Pattern 15)
 * - Initialization guard (Pattern 16)
 * - No user input rendered to DOM (Pattern 17 - N/A for pure BL)
 * - CoreEventBus central freeze (Pattern 18 DRY)
 * - Conductor single publication (Pattern 20)
 *
 * PERFORMANCE CHARACTERISTICS:
 * - Base price retrieval: O(1) conductor delegation
 * - Selection price lookup: O(1) PPD Map lookup via conductor
 * - Total price calculation: O(1) aggregation via conductor
 * - Expected execution time: ~1-5ms
 *
 * All external communication flows through md-config-core-conductor.js delegation.
 *
 * Dependencies:
 * - md-config-core-conductor.js (PPD delegation, state access)
 * - md-config-core-event-bus.js (event system)
 * - md-config-core-constants.js (event constants)
 *
 * This module is used by:
 * - md-config-core-conductor.js (primary conductor)
 */

/** Module uses scoped pattern for Shopify compatibility */
(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 1 & 19: GLOBAL CONSTANTS USAGE (10 points each)
  // ============================================================================
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;
  const EVENTS = CONSTANTS.EVENTS;
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;

  // ============================================================================
  // MODULE-SPECIFIC CONSTANTS
  // ============================================================================
  const PRICE_CONSTANTS = Object.freeze({
    DEFAULT_PRICE: 0,
    DECIMAL_PLACES: 2,
    CURRENCY_SYMBOL: '$',
  });

  const PRICE_FORMAT = Object.freeze({
    LOCALE: 'en-US',
    STYLE: 'currency',
    CURRENCY: 'USD',
    MIN_FRACTION_DIGITS: 2,
    MAX_FRACTION_DIGITS: 2,
  });

  const PRICE_REGEX = Object.freeze({
    COMMA_REMOVAL: /,/g,
    PRICE_EXTRACTION: /\$(\d+(?:\.\d{2})?)/,
  });

  // Phase 0 shared formatter. Resolved at CALL time, not load time: core-utils
  // is guaranteed to load first in production (sections/md-configurator.liquid
  // — utils at :1216, this module at :1376), but deferring the lookup keeps the
  // module loadable in a bare test harness.
  //
  // MIN/MAX_FRACTION_DIGITS above stay at 2 deliberately. Intl formats to two
  // decimals and the helper strips ".00" afterwards; setting the digits to 0
  // here instead would ROUND $1,770.90 to $1,771 and desync the display from
  // what the cart charges. See utils.dropWholeDollarCents.
  function dropWholeDollarCents(formatted) {
    const utils = window.MurphyDoorConfigurator.utils;
    return utils && utils.dropWholeDollarCents
      ? utils.dropWholeDollarCents(formatted)
      : formatted;
  }

  // Phase 0 shared price-label vocabulary + rule, resolved at CALL time for
  // the same reason as dropWholeDollarCents above: core-utils is guaranteed to
  // load first in production (sections/md-configurator.liquid), but deferring
  // the lookup keeps this module loadable in a bare test harness.
  //
  // The fallbacks below are the literal strings ONLY so that a harness without
  // core-utils still renders a sane label. Production always takes the utils
  // path — do not treat these as a second source of truth.
  function priceLabels() {
    const utils = window.MurphyDoorConfigurator.utils;
    return (
      (utils && utils.PRICE_LABELS) || {
        BASE: 'Starting at',
        CONFIGURED: 'Your Price',
      }
    );
  }

  function resolvePriceLabel(totalPrice, basePrice) {
    const utils = window.MurphyDoorConfigurator.utils;
    return utils && utils.resolvePriceLabel
      ? utils.resolvePriceLabel(totalPrice, basePrice)
      : priceLabels().BASE;
  }

  const PRICE_SOURCES = Object.freeze({
    STATE_MAP: 'state_map',
    VARIANT_PPD: 'variant_ppd',
    PRODUCT_PPD: 'product_ppd',
  });

  // ============================================================================
  // PATTERN 13: PERFORMANCE THRESHOLDS (10 points)
  // ============================================================================
  const PERFORMANCE_THRESHOLDS = Object.freeze({
    BASE_PRICE: 10, // ms - O(1) conductor delegation
    TOTAL_PRICE: 50, // ms - aggregation via conductor
    SELECTION_PRICE: 10, // ms - O(1) PPD Map lookup
    PRICE_UPDATE: 50, // ms - full coordination cycle
  });

  const ERROR_MESSAGES = Object.freeze({
    CONDUCTOR_REQUIRED:
      'Conductor parameter is REQUIRED - all Phase 2 modules must use conductor for PPD access',
    MISSING_DATA: 'Missing data parameter for price calculation',
    MISSING_STATE: 'Missing state parameter for price calculation',
    MISSING_DEPENDENCIES: 'Missing dependencies for price update coordination',
    INVALID_STATE: 'Invalid selection state',
    STATE_MANAGER_UNAVAILABLE: 'State manager not available',
    UI_UPDATER_UNAVAILABLE: 'UI updater module not available',
    NOT_BOOTSTRAPPED: 'Price calculator called before bootstrap complete',
  });

  // ============================================================================
  // PATTERN 16: INITIALIZATION GUARD (10 points)
  // ============================================================================
  let isInitialized = false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // ============================================================================
  let bootstrapComplete = false;

  // ============================================================================
  // PATTERN 15: CLEANUP ARRAYS (10 points)
  // ============================================================================
  const cleanupHandlers = [];

  // ============================================================================
  // PATTERN 2: publishEvent() ASYNC HELPER (10 points)
  // PATTERN 11: ASYNC EVENT PUBLISHING (10 points)
  // NOTE: Object.freeze() NOT needed - CoreEventBus.publish() freezes centrally (Pattern 18 DRY)
  // ============================================================================
  async function publishEvent(eventType, eventData) {
    try {
      if (CoreEventBus?.publish) {
        await CoreEventBus.publish(eventType, eventData);
      } else {
        isDev() &&
          console.warn(
            `[PriceCalculator] Event bus unavailable, failed to publish: ${eventType}`
          );
      }
    } catch (error) {
      isDev() &&
        console.error(
          `[PriceCalculator] Error publishing ${eventType}:`,
          error
        );
    }
  }

  // ============================================================================
  // PATTERN 4: EVENT CONSUMER HANDLERS (10 points)
  // PATTERN 6: BOOTSTRAP SUBSCRIPTION AT MODULE LOAD TIME (10 points)
  // ============================================================================
  if (CoreEventBus) {
    // Subscribe to bootstrap completion at module load time (race protection)
    const unsubBootstrap = CoreEventBus.subscribe(
      EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        isDev() &&
          console.log('[PriceCalculator] Bootstrap complete event received');
        bootstrapComplete = true;
        isInitialized = true;

        // Publish module ready event
        publishEvent(EVENTS.MODULE_PRICE_CALCULATOR_READY, {
          module: 'priceCalculator',
          timestamp: Date.now(),
        });
      }
    );
    cleanupHandlers.push(unsubBootstrap);

    isDev() &&
      console.log(
        '[PriceCalculator] Subscribed to bootstrap event at module load time'
      );
  }

  // ============================================================================
  // MODULE IMPLEMENTATION
  // ============================================================================
  window.MurphyDoorConfigurator.priceCalculator = {
    /**
     * Get base product price.
     * PPD-FIRST: Uses conductor delegation for O(1) access.
     *
     * @param {Object} conductor - REQUIRED conductor instance
     * @returns {number} Base product price
     */
    getBasePrice(conductor) {
      const startTime = window.performance.now();
      const METHOD_SIGNATURE = 'priceCalculator.getBasePrice';

      // PATTERN 6: Bootstrap runtime protection
      if (!bootstrapComplete) {
        isDev() &&
          console.warn(`[PriceCalculator] ${ERROR_MESSAGES.NOT_BOOTSTRAPPED}`);
        return PRICE_CONSTANTS.DEFAULT_PRICE;
      }

      // PPD-FIRST ENFORCEMENT: Conductor required for PPD access
      if (!conductor || !conductor.getProductStructure) {
        const error = new Error(
          `CRITICAL: ${ERROR_MESSAGES.CONDUCTOR_REQUIRED} (getBasePrice)`
        );

        publishEvent(EVENTS.BUSINESS_PRICE_CALCULATION_FAILED, {
          error: error.message,
          source: METHOD_SIGNATURE,
          timestamp: Date.now(),
        });

        throw error;
      }

      const struct = conductor.getProductStructure();
      const basePrice =
        struct?.baseProduct?.variant?.price || PRICE_CONSTANTS.DEFAULT_PRICE;

      // PATTERN 12: Performance tracking
      const executionTime = window.performance.now() - startTime;

      // Publish base price retrieved event
      publishEvent(EVENTS.BUSINESS_BASE_PRICE_RETRIEVED, {
        basePrice,
        executionTime,
        timestamp: Date.now(),
        source: METHOD_SIGNATURE,
      });

      // PATTERN 13: Performance threshold check
      if (executionTime > PERFORMANCE_THRESHOLDS.BASE_PRICE) {
        publishEvent(EVENTS.PERFORMANCE_PRICE_CALCULATION_SLOW, {
          executionTime,
          threshold: PERFORMANCE_THRESHOLDS.BASE_PRICE,
          source: METHOD_SIGNATURE,
          timestamp: Date.now(),
        });
      }

      return basePrice;
    },

    /**
     * Calculate total price (base + applied + browsing).
     * Implements 5-stage event lifecycle: STARTED -> VALIDATION -> PROGRESS -> COMPLETED/FAILED
     * PPD-FIRST: Uses conductor delegation for O(1) operations.
     *
     * @param {Object} data - Selection data
     * @param {Object} conductor - REQUIRED conductor instance
     * @returns {number} Total price
     */
    calculateTotalPrice(data, conductor) {
      const startTime = window.performance.now();
      const METHOD_SIGNATURE = 'priceCalculator.calculateTotalPrice';

      // PATTERN 6: Bootstrap runtime protection
      if (!bootstrapComplete) {
        isDev() &&
          console.warn(`[PriceCalculator] ${ERROR_MESSAGES.NOT_BOOTSTRAPPED}`);
        return PRICE_CONSTANTS.DEFAULT_PRICE;
      }

      try {
        // ================================================================
        // STAGE 1: START - Announce operation beginning
        // ================================================================
        publishEvent(EVENTS.BUSINESS_PRICE_CALCULATION_STARTED, {
          state: data?.state,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // ================================================================
        // STAGE 2: VALIDATION - Validate inputs
        // ================================================================
        if (!conductor || !conductor.getAppliedSelectionsTotalPrice) {
          const errorMsg = ERROR_MESSAGES.CONDUCTOR_REQUIRED;
          publishEvent(EVENTS.BUSINESS_PRICE_CALCULATION_VALIDATION, {
            valid: false,
            error: errorMsg,
            source: METHOD_SIGNATURE,
            timestamp: Date.now(),
          });
          throw new Error(`CRITICAL: ${errorMsg} (calculateTotalPrice)`);
        }

        publishEvent(EVENTS.BUSINESS_PRICE_CALCULATION_VALIDATION, {
          valid: true,
          source: METHOD_SIGNATURE,
          timestamp: Date.now(),
        });

        // ================================================================
        // STAGE 3: PROGRESS - Business logic computation
        // ================================================================
        // Get base price via conductor delegation
        const basePrice = this.parsePrice(this.getBasePrice(conductor));

        // Get applied selections total via conductor delegation (O(1) or optimized iteration)
        const appliedSelectionsPrice =
          conductor.getAppliedSelectionsTotalPrice();

        // Get ALL browsing selections total via conductor delegation
        // STATE_MAPS ensures no double-counting: a category is in appliedSelections OR
        // browsingSelections, never both (see state-manager lines 1206-1225)
        const browsingSelectionsPrice =
          conductor.getBrowsingSelectionsTotalPrice();

        publishEvent(EVENTS.BUSINESS_PRICE_CALCULATION_PROGRESS, {
          stage: 'aggregation',
          basePrice,
          appliedSelectionsPrice,
          browsingSelectionsPrice,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // Publish applied selections price event (downstream consumers)
        publishEvent(EVENTS.BUSINESS_APPLIED_SELECTIONS_PRICE_RETRIEVED, {
          appliedSelectionsPrice,
          browsingSelectionsPrice,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        const totalPrice =
          basePrice + appliedSelectionsPrice + browsingSelectionsPrice;

        // ================================================================
        // STAGE 4: SUCCESS/COMPLETED
        // ================================================================
        const executionTime = window.performance.now() - startTime;

        // PATTERN 13: Performance threshold check
        if (executionTime > PERFORMANCE_THRESHOLDS.TOTAL_PRICE) {
          publishEvent(EVENTS.PERFORMANCE_PRICE_CALCULATION_SLOW, {
            executionTime,
            threshold: PERFORMANCE_THRESHOLDS.TOTAL_PRICE,
            source: METHOD_SIGNATURE,
            timestamp: Date.now(),
          });
        }

        publishEvent(EVENTS.BUSINESS_TOTAL_PRICE_CALCULATED, {
          totalPrice,
          basePrice,
          appliedSelectionsPrice,
          browsingSelectionsPrice,
          executionTime,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // Dev logging for debugging
        isDev() &&
          console.log('[PriceCalculator] Total price calculated:', {
            totalPrice: this.formatPrice(totalPrice),
            executionTime: `${executionTime.toFixed(2)}ms`,
            breakdown: {
              base: this.formatPrice(basePrice),
              applied: this.formatPrice(appliedSelectionsPrice),
              browsing: this.formatPrice(browsingSelectionsPrice),
            },
          });

        return totalPrice;
      } catch (error) {
        // ================================================================
        // STAGE 5: FAILED
        // ================================================================
        publishEvent(EVENTS.BUSINESS_PRICE_CALCULATION_FAILED, {
          error: error.message || error,
          data,
          duration: window.performance.now() - startTime,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        throw error; // Re-throw for caller to handle
      }
    },

    /**
     * Parse a price value to a numeric amount.
     * Handles strings (with currency symbols/commas), numbers, and edge cases.
     *
     * @param {string|number|null|undefined} price - The price to parse
     * @returns {number} Parsed numeric price
     */
    parsePrice(price) {
      // Explicitly handle null, undefined, and empty string as valid $0
      if (price === null || price === undefined || price === '') return 0;

      let numericPrice;

      if (typeof price === 'string') {
        // Remove currency symbols and commas before parsing
        numericPrice = parseFloat(price.replace(/[,$]/g, ''));
      } else if (typeof price === 'number') {
        numericPrice = price;
      } else {
        // Invalid type - throw error instead of silently returning 0
        throw new Error(
          `Invalid price type: ${typeof price} for value '${price}'`
        );
      }

      // Check if parsing resulted in NaN - this means invalid data like "N/A" or "Free"
      if (isNaN(numericPrice)) {
        throw new Error(
          `Failed to parse a valid number from price: '${price}'`
        );
      }

      return numericPrice;
    },

    /**
     * Calculate selection price from product/variant data.
     * PPD-FIRST: Uses O(1) PPD lookups via conductor.
     * Implements 5-stage event lifecycle.
     *
     * @param {Object} data - Selection data with productId/variantId
     * @param {Object} conductor - REQUIRED conductor instance
     * @returns {number} Selection price
     */
    calculateSelectionPrice(data, conductor) {
      const startTime = window.performance.now();
      const METHOD_SIGNATURE = 'priceCalculator.calculateSelectionPrice';

      // PATTERN 6: Bootstrap runtime protection
      if (!bootstrapComplete) {
        isDev() &&
          console.warn(`[PriceCalculator] ${ERROR_MESSAGES.NOT_BOOTSTRAPPED}`);
        return PRICE_CONSTANTS.DEFAULT_PRICE;
      }

      try {
        // STAGE 1: START (implicit in caller context)

        // STAGE 2: VALIDATION
        if (
          !conductor ||
          !conductor.getVariantById ||
          !conductor.getProductById
        ) {
          throw new Error(
            `CRITICAL: ${ERROR_MESSAGES.CONDUCTOR_REQUIRED} (calculateSelectionPrice)`
          );
        }

        if (!data.productId && !data.variantId) {
          return 0;
        }

        // STAGE 3: PROGRESS - Price lookup
        // First try to get price from state maps for more accurate data
        const stateMaps = conductor.getStateMaps();
        const selections = stateMaps.selectionStates;
        const key = data.variantId
          ? `variant_${data.variantId}`
          : `product_${data.productId}`;
        const statePrice = selections.get(key)?.price;

        if (statePrice) {
          const parsedPrice = this.parsePrice(statePrice);

          // STAGE 4: SUCCESS
          publishEvent(EVENTS.BUSINESS_SELECTION_PRICE_CALCULATED, {
            price: parsedPrice,
            priceSource: PRICE_SOURCES.STATE_MAP,
            selectionKey: key,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });

          return parsedPrice;
        }

        // PPD via conductor (O(1) lookups)
        let price = 0;
        let priceSource = '';

        // Variant selections take precedence
        if (data.variantId) {
          const variant = conductor.getVariantById(data.variantId);
          price = this.parsePrice(variant?.price);
          priceSource = PRICE_SOURCES.VARIANT_PPD;
        } else if (data.productId) {
          // Product selection
          const product = conductor.getProductById(data.productId);
          price = this.parsePrice(product?.price);
          priceSource = PRICE_SOURCES.PRODUCT_PPD;
        }

        // STAGE 4: SUCCESS
        publishEvent(EVENTS.BUSINESS_SELECTION_PRICE_CALCULATED, {
          price,
          priceSource,
          productId: data.productId,
          variantId: data.variantId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // PATTERN 12 & 13: Performance tracking with thresholds
        const executionTime = window.performance.now() - startTime;
        if (executionTime > PERFORMANCE_THRESHOLDS.SELECTION_PRICE) {
          publishEvent(EVENTS.PERFORMANCE_PRICE_CALCULATION_SLOW, {
            executionTime,
            threshold: PERFORMANCE_THRESHOLDS.SELECTION_PRICE,
            source: METHOD_SIGNATURE,
            timestamp: Date.now(),
          });
        }

        return price;
      } catch (error) {
        // STAGE 5: FAILED
        publishEvent(EVENTS.BUSINESS_PRICE_CALCULATION_FAILED, {
          error: error.message || error,
          data,
          duration: window.performance.now() - startTime,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        throw error; // Re-throw to ensure the entire price calculation fails properly
      }
    },

    /**
     * Format a price amount as a currency string (e.g., "$1,234.56").
     *
     * Whole-dollar amounts drop the ".00" ("$1,750", not "$1,750.00"); real
     * cents are preserved verbatim. See utils.dropWholeDollarCents.
     *
     * @param {number|string} amount - The amount to format
     * @returns {string} Formatted price string with currency symbol
     */
    formatPrice(amount) {
      const numericAmount =
        typeof amount === 'string'
          ? parseFloat(amount.replace(PRICE_REGEX.COMMA_REMOVAL, ''))
          : parseFloat(amount);

      // Guard against NaN to prevent "NaN" from appearing in UI
      const safeAmount = Number.isFinite(numericAmount) ? numericAmount : 0;

      return dropWholeDollarCents(
        new Intl.NumberFormat(PRICE_FORMAT.LOCALE, {
          style: PRICE_FORMAT.STYLE,
          currency: PRICE_FORMAT.CURRENCY,
          minimumFractionDigits: PRICE_FORMAT.MIN_FRACTION_DIGITS,
          maximumFractionDigits: PRICE_FORMAT.MAX_FRACTION_DIGITS,
        }).format(safeAmount)
      );
    },

    /**
     * Format a price amount without currency symbol (e.g., "1,234.56").
     *
     * Whole-dollar amounts drop the ".00" ("1,750", not "1,750.00"); real
     * cents are preserved verbatim. See utils.dropWholeDollarCents.
     *
     * 🚨 This feeds the MAIN price display, whose "$" comes from a CSS
     * ::before (section-md-configurator.css:568 / :858) and never reaches
     * textContent — so the string here is bare, e.g. "1,750".
     *
     * @param {number|string} amount - The amount to format
     * @returns {string} Formatted price string without currency symbol
     */
    formatPriceWithoutCurrency(amount) {
      const numericAmount =
        typeof amount === 'string'
          ? parseFloat(amount.replace(PRICE_REGEX.COMMA_REMOVAL, ''))
          : parseFloat(amount);

      // Guard against NaN to prevent "NaN" from appearing in UI
      const safeAmount = Number.isFinite(numericAmount) ? numericAmount : 0;

      return dropWholeDollarCents(
        new Intl.NumberFormat(PRICE_FORMAT.LOCALE, {
          minimumFractionDigits: PRICE_FORMAT.MIN_FRACTION_DIGITS,
          maximumFractionDigits: PRICE_FORMAT.MAX_FRACTION_DIGITS,
        }).format(safeAmount)
      );
    },

    /**
     * Coordinates a full price update cycle including calculation and UI delegation.
     * Implements 5-stage event lifecycle: STARTED -> VALIDATION -> PROGRESS -> COMPLETED/FAILED
     *
     * Extracted from md-config-core-conductor.js handlePriceUpdates() to properly
     * delegate business logic to the appropriate business layer.
     *
     * @param {Object} data - Selection data
     * @param {string} state - Selection state
     * @param {Object} dependencies - { SELECTION_STATES, stateManager, uiUpdater, eventBus, conductor }
     * @returns {Object} Result with success status and price data
     */
    coordinatePriceUpdate(data, state, dependencies) {
      const METHOD_SIGNATURE = 'priceCalculator.coordinatePriceUpdate';
      const startTime = performance.now();

      // PATTERN 6: Bootstrap runtime protection
      if (!bootstrapComplete) {
        isDev() &&
          console.warn(`[PriceCalculator] ${ERROR_MESSAGES.NOT_BOOTSTRAPPED}`);
        return { success: false, error: ERROR_MESSAGES.NOT_BOOTSTRAPPED };
      }

      try {
        // ================================================================
        // STAGE 1: START - Announce operation beginning
        // ================================================================
        publishEvent(EVENTS.BUSINESS_PRICE_CALCULATION_STARTED, {
          operation: 'coordinatePriceUpdate',
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // ================================================================
        // STAGE 2: VALIDATION - Validate all required inputs
        // ================================================================
        if (!data) {
          const errorMsg = ERROR_MESSAGES.MISSING_DATA;
          publishEvent(EVENTS.BUSINESS_PRICE_CALCULATION_VALIDATION, {
            valid: false,
            error: errorMsg,
            source: METHOD_SIGNATURE,
            timestamp: Date.now(),
          });
          throw new Error(errorMsg);
        }

        if (!state) {
          const errorMsg = ERROR_MESSAGES.MISSING_STATE;
          publishEvent(EVENTS.BUSINESS_PRICE_CALCULATION_VALIDATION, {
            valid: false,
            error: errorMsg,
            source: METHOD_SIGNATURE,
            timestamp: Date.now(),
          });
          throw new Error(errorMsg);
        }

        if (!dependencies) {
          const errorMsg = ERROR_MESSAGES.MISSING_DEPENDENCIES;
          publishEvent(EVENTS.BUSINESS_PRICE_CALCULATION_VALIDATION, {
            valid: false,
            error: errorMsg,
            source: METHOD_SIGNATURE,
            timestamp: Date.now(),
          });
          throw new Error(errorMsg);
        }

        const { SELECTION_STATES, stateManager, uiUpdater, conductor } =
          dependencies;

        if (!Object.values(SELECTION_STATES).includes(state)) {
          const errorMsg = `${ERROR_MESSAGES.INVALID_STATE}: ${state}`;
          publishEvent(EVENTS.BUSINESS_PRICE_CALCULATION_VALIDATION, {
            valid: false,
            error: errorMsg,
            source: METHOD_SIGNATURE,
            timestamp: Date.now(),
          });
          throw new Error(errorMsg);
        }

        if (!stateManager) {
          const errorMsg = ERROR_MESSAGES.STATE_MANAGER_UNAVAILABLE;
          publishEvent(EVENTS.BUSINESS_PRICE_CALCULATION_VALIDATION, {
            valid: false,
            error: errorMsg,
            source: METHOD_SIGNATURE,
            timestamp: Date.now(),
          });
          throw new Error(errorMsg);
        }

        if (!uiUpdater) {
          const errorMsg = ERROR_MESSAGES.UI_UPDATER_UNAVAILABLE;
          publishEvent(EVENTS.BUSINESS_PRICE_CALCULATION_VALIDATION, {
            valid: false,
            error: errorMsg,
            source: METHOD_SIGNATURE,
            timestamp: Date.now(),
          });
          throw new Error(errorMsg);
        }

        publishEvent(EVENTS.BUSINESS_PRICE_CALCULATION_VALIDATION, {
          valid: true,
          source: METHOD_SIGNATURE,
          timestamp: Date.now(),
        });

        // ================================================================
        // STAGE 3: PROGRESS - Calculate and coordinate
        // ================================================================
        // Calculate total price through this module's dedicated calculator
        const totalPrice = this.calculateTotalPrice(data, conductor);

        // Base price for the label comparison below. parsePrice() is required,
        // not optional: getBasePrice() returns the PPD's pre-formatted dollar
        // STRING ("1,440.00") while calculateTotalPrice() returns a NUMBER.
        const basePrice = this.parsePrice(this.getBasePrice(conductor));

        // Retained for the telemetry/events below ONLY. This is no longer what
        // picks the label — see resolvePriceLabel() for why counting
        // selections got it wrong on every $0 "Default Title" add-on.
        const hasAnySelections = conductor.hasAnySelections();

        publishEvent(EVENTS.BUSINESS_PRICE_CALCULATION_PROGRESS, {
          stage: 'ui_delegation',
          totalPrice,
          hasAnySelections,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // Prepare price update data with pre-formatted values
        // CCP COMPLIANCE: Price formatting done here in business logic layer
        // so ui-updater receives ready-to-render strings (no cross-module calls needed)
        // DRY: Single label determination used for BOTH main and summary displays
        const priceLabel = resolvePriceLabel(totalPrice, basePrice);

        const priceUpdateData = {
          label: priceLabel,
          amount: totalPrice,
          formattedAmount: this.formatPriceWithoutCurrency(totalPrice),
          // Summary mirrors main display: always populated, same label logic
          summaryLabel: priceLabel,
          formattedSummary: this.formatPriceWithoutCurrency(totalPrice),
        };

        // PATTERN 10: UI Update via delegation (through provided UI module)
        uiUpdater.batchPriceUpdate(priceUpdateData);

        // ================================================================
        // STAGE 4: SUCCESS/COMPLETED
        // ================================================================
        const executionTime = performance.now() - startTime;

        // PATTERN 13: Performance threshold check
        if (executionTime > PERFORMANCE_THRESHOLDS.PRICE_UPDATE) {
          publishEvent(EVENTS.PERFORMANCE_PRICE_CALCULATION_SLOW, {
            executionTime,
            threshold: PERFORMANCE_THRESHOLDS.PRICE_UPDATE,
            source: METHOD_SIGNATURE,
            timestamp: Date.now(),
          });
        }

        publishEvent(EVENTS.BUSINESS_PRICE_CALCULATION_COMPLETED, {
          timestamp: Date.now(),
          totalPrice,
          hasAnySelections,
          selectionState: state,
          executionTime,
          source: METHOD_SIGNATURE,
        });

        return {
          success: true,
          totalPrice,
          hasAnySelections,
        };
      } catch (error) {
        // ================================================================
        // STAGE 5: FAILED - Error handling with fallback
        // ================================================================

        // Attempt to show fallback pricing if possible
        if (dependencies && dependencies.uiUpdater) {
          try {
            let fallbackAmount = PRICE_CONSTANTS.DEFAULT_PRICE;
            // Defensively check conductor before using it in fallback.
            if (
              dependencies.conductor &&
              dependencies.conductor.getProductStructure
            ) {
              fallbackAmount = this.getBasePrice(dependencies.conductor);
            }
            // fallbackAmount IS the base price, so BASE is correct here by
            // construction, not merely a safe default.
            dependencies.uiUpdater.batchPriceUpdate({
              label: priceLabels().BASE,
              amount: fallbackAmount,
              formattedAmount: this.formatPriceWithoutCurrency(fallbackAmount),
              summaryLabel: priceLabels().BASE,
              formattedSummary: this.formatPriceWithoutCurrency(fallbackAmount),
            });
          } catch (fallbackError) {
            isDev() &&
              console.warn(
                '[PriceCalculator] Fallback pricing failed:',
                fallbackError
              );
          }
        }

        // Publish error event
        publishEvent(EVENTS.BUSINESS_PRICE_CALCULATION_FAILED, {
          error: error.message || error,
          duration: performance.now() - startTime,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        return {
          success: false,
          error: error.message,
        };
      }
    },

    // ========================================================================
    // PATTERN 15: CLEANUP (10 points)
    // ========================================================================
    /**
     * Cleanup all event subscriptions and reset module state.
     * Called during system shutdown or module teardown.
     */
    cleanup() {
      isDev() && console.log('[PriceCalculator] Cleaning up resources');

      // Unsubscribe all event handlers
      cleanupHandlers.forEach((unsubscribe) => {
        try {
          unsubscribe();
        } catch (e) {
          /* ignore cleanup errors */
        }
      });
      cleanupHandlers.length = 0;

      isInitialized = false;
      bootstrapComplete = false;

      publishEvent(EVENTS.MODULE_PRICE_CALCULATOR_CLEANUP, {
        module: 'priceCalculator',
        timestamp: Date.now(),
      });
    },
  };
})(window);
